/* import __COLOCATED_TEMPLATE__ from './project-details-info-summary-ledger.hbs'; */
import Component from '@glimmer/component';
import { APPLICATION, APPLICATION_SOURCE } from 'sh-www/models/application';
import { FEE_STRUCTURE } from 'sh-www/models/fee-structure';
import { SH_BILLABLE_LINE_ITEM } from 'sh-www/models/sh-billable-line-item';
import {
  PROJECT_DETAILS__APPLICATION_EDITABLE_EXPENSES_STATES_SELLER,
  PROJECT_DETAILS__APPLICATION_EDITABLE_PRICE_STATES_BUYER,
} from 'sh-www/constants/routes/project-details';
import clsx from 'clsx';

const CLASS_NAME = 'pricing-details-info-summary-ledger';
const BUTTON_CLASS_NAME = `${CLASS_NAME}__button`;
const BUTTON_DISABLED_CLASS_NAME = `${BUTTON_CLASS_NAME}--disabled`;

const SELLER_BASIC_FEES_COVERED_ITEM = {
  amount: 0,
  description: 'Service Fee Covered By Client',
  type: FEE_STRUCTURE.FEE_TYPES.BASE_FEE,
};

const SELLER_SELECT_FEES_COVERED_ITEM = {
  amount: 0,
  description: 'Select Fee Covered By Client',
  type: FEE_STRUCTURE.FEE_TYPES.SELECT_FEE,
};

const { [APPLICATION.SIMPLE_STATUS.PENDING]: PENDING_SIMPLE_STATUS } =
  APPLICATION.SIMPLE_STATUS_MAPPING;

class ProjectDetailsInfoSummaryLedger extends Component {
  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME
    );
  }

  get buttonClassNames() {
    return clsx(
      BUTTON_CLASS_NAME,
      this.args.disableButtons && BUTTON_DISABLED_CLASS_NAME
    );
  }

  get showBuyerFees() {
    return (this.args.user.isAdmin || this.args.user.isBuyer)
      && this.buyerFees.length > 0;
  }

  get showSellerFees() {
    return (this.args.user.isAdmin || this.args.user.isSeller)
      && this.sellerFees.length > 0;
  }

  get projectPriceLabel() {
    const { application, isSellerApplicationView } = this.args;

    const showOffer =
      isSellerApplicationView ||
      (application.direct
        && application.source === APPLICATION_SOURCE.QUOTE
        && PENDING_SIMPLE_STATUS.includes(application.status)
      );

    return showOffer
      ? 'Current offer'
      : 'Project price';
  }

  get buyerFeesDropdownLabel() {
    return this.args.user.isAdmin ? 'Buyer Fees' : 'Fees';
  }

  get sellerFeesDropdownLabel() {
    return this.args.user.isAdmin ? 'Seller Fees' : 'Fees';
  }

  get sellerFees() {
    const { sellerFeesCovered, consolidatedSellerFees, select } = this.args.application;
    const { isAdmin, isSeller } = this.args.user;
    if (isAdmin || isSeller) {
      const feeBreakdown = consolidatedSellerFees.map(feeItem => feeItem);
      if (sellerFeesCovered) {
        feeBreakdown.push(SELLER_BASIC_FEES_COVERED_ITEM);

        if (select) {
          feeBreakdown.push(SELLER_SELECT_FEES_COVERED_ITEM);
        }
      }

      return feeBreakdown;
    }

    return [];
  }

  get sellerFeesTotalAmount() {
    if (!this.args.isSellerApplicationView) {
      return this.args.application.sellerFeesTotalAmount;
    }

    return this.args.application.shPaymentLineItems.reduce((carry, lineItem) => {
        return lineItem.type !== SH_BILLABLE_LINE_ITEM.TYPES.BASE ? carry + lineItem.amount : carry;
    }, 0);
  }

  get buyerFees() {
    const { sellerFeesCovered, consolidatedBuyerFees } = this.args.application;
    const { isAdmin, isBuyer } = this.args.user;
    if (isAdmin || isBuyer) {
      return consolidatedBuyerFees.reduce((accum, feeItem) => {
        const { type } = feeItem;
        const isCoveredServiceFee = type === FEE_STRUCTURE.FEE_TYPES.BASE_FEE && sellerFeesCovered;
        const isCoveredSelectFee = type === FEE_STRUCTURE.FEE_TYPES.SELECT_FEE && sellerFeesCovered;

        if (isCoveredServiceFee) {
          feeItem.description = "Combined Service Fees";
        }

        if (isCoveredSelectFee) {
          feeItem.description = "Combined Select Fees";
        }

        accum.push(feeItem);
        return accum;
      }, []);
    }

    return [];
  }

  get sellerTotalAmount() {
    return this.args.application.shPaymentLineItems.reduce((carry, lineItem) => {
      return carry + lineItem.amount;
    }, 0);
  }

  get buyerTotalAmount() {
    return this.args.application.shInvoiceLineItems.reduce((carry, lineItem) => {
      return carry + lineItem.amount;
    }, 0);
  }

  get hasExistingExpenses() {
    return this.args.application.expenseProjectLineItems.length > 0;
  }

  get canEditExpenses() {
    return this.args.user.isSeller
      && this.args.application.isAllowExpenses
      && PROJECT_DETAILS__APPLICATION_EDITABLE_EXPENSES_STATES_SELLER.includes(this.args.application.status);
  }

  get showAddExpenses() {
    const { status } = this.args.application;
    return this.canEditExpenses
      && !this.hasExistingExpenses
      && APPLICATION.STATUS.ACTIVE === status;
  }

  get showUpdateExpenses() {
    const { status } = this.args.application;
    return this.canEditExpenses
      && this.hasExistingExpenses
      && APPLICATION.STATUS.ACTIVE === status;
  }

  get otherItemsTotalAmount() {
    return this.args.otherItems.reduce((carry, otherLineItem) => {
      return carry + otherLineItem.amount;
    }, 0);
  }

  get showEditProjectPrice() {
    return this.args.user.isBuyer && PROJECT_DETAILS__APPLICATION_EDITABLE_PRICE_STATES_BUYER.includes(this.args.application.status);
  }
}

export default ProjectDetailsInfoSummaryLedger;
